import { useCallback, useEffect, useMemo } from "react";
import { animate, motion, useMotionValue, useReducedMotion } from "framer-motion";
import { useDemo } from "../../context/DemoContext";
import { people, storyOrder } from "../../data/demoContent";
import { IPhoneFrame, StatusBar, SCREEN_H } from "../IPhoneFrame";
import { StoryCircle } from "./StoryCircle";
import { StoryViewer } from "./StoryViewer";
import { ChatBottomSheet } from "./ChatBottomSheet";

const StoryStrip = ({ person, onOpen }) => (
  <button type="button" onClick={onOpen} data-testid="story-strip" className="glass glass-light absolute inset-x-4 top-[60px] z-10 flex items-center gap-2.5 rounded-2xl px-2 py-1.5 text-left">
    <img src={person.story.image} alt="" className="h-9 w-7 rounded-md object-cover" />
    <span className="flex-1 text-[12px] font-semibold text-[#08090D]">{person.isMe ? "Your story" : `${person.name}'s story`} · {person.story.time}</span>
    <span className="text-[12px] font-semibold text-[#7866E8]">View</span>
  </button>
);

export const PhoneDemo = () => {
  const { selected, setSelected, mode, setMode, zoom, setZoom, markInteraction } = useDemo();
  const reduce = useReducedMotion();
  const H = SCREEN_H;
  const snaps = useMemo(() => ({ min: H - 74, peek: Math.round(H * 0.6), split: Math.round(H * 0.46), expanded: 112 }), [H]);
  const y = useMotionValue(snaps[mode]);

  useEffect(() => {
    const ctl = animate(y, snaps[mode], reduce ? { duration: 0 } : { type: "spring", stiffness: 300, damping: 34 });
    return () => ctl.stop();
  }, [mode, snaps, y, reduce]);

  const goTo = useCallback(
    (m) => {
      setMode(m);
      if (m !== "min") setZoom(false);
      animate(y, snaps[m], reduce ? { duration: 0 } : { type: "spring", stiffness: 300, damping: 34 });
    },
    [setMode, setZoom, snaps, y, reduce]
  );

  const select = (id) => {
    markInteraction();
    setSelected(id);
    if (people[id].isMe && (mode === "split" || mode === "expanded")) goTo("peek");
  };

  const person = people[selected];
  const expanded = mode === "expanded";

  return (
    <IPhoneFrame testId="phone-demo">
      <StatusBar />
      <div className="phone-upper absolute inset-0" aria-hidden />
      <motion.div className="absolute inset-x-0 top-0" animate={{ opacity: expanded || zoom ? 0 : 1 }} transition={{ duration: 0.3 }}>
        <h3 className="px-6 pt-[64px] text-[29px] font-extrabold leading-none tracking-[-0.04em] text-[#08090D]">Let's Stay Connected</h3>
        <div className="no-scrollbar mt-4 flex gap-3 overflow-x-auto px-5 pb-1" role="group" aria-label="Stories">
          {storyOrder.map((id) => (
            <StoryCircle key={id} person={people[id]} selected={selected === id} onSelect={select} />
          ))}
        </div>
      </motion.div>
      <StoryViewer person={person} zoom={zoom} bottom={86} hidden={expanded} />
      {expanded && <StoryStrip person={person} onOpen={() => { markInteraction(); goTo("min"); }} />}
      <ChatBottomSheet y={y} H={H} snaps={snaps} goTo={goTo} />
    </IPhoneFrame>
  );
};
